import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  buildStationTimingEmptySelection,
  formatStationTimingHourWindow,
  STATION_TIMING_PREVIEW_MODAL_WIDTH,
  type IStationTimingPreviewSelection,
} from "@ryde/controls";
import {
  useModalConfirm,
  useRideDtoActions,
  useRideEmptyStationsAllowed,
} from "@ryde/redux";
import {
  collapseOptionalRouteLegsAcrossWaypoints,
  ConfirmResult,
  errors,
  expandCollapsedRouteLegsAcrossWaypoints,
  filterRealRouteStations,
  getRidePlannedLegSeconds,
  getRideStationsTimeBulkOffsetMinutes,
  getRideStationTimingComparisonBasis,
  isUpdatedStationTimeInsideShiftLimits,
  Log,
  MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION,
  RideStationPanelMode,
  STATION_TIMING_RIDE_COMPARABLE_HOURS_SPREAD,
  STATION_TIMING_SOURCE_RIDE,
  StationTimingApi,
  updateRideMetadataStationsTimeBulk,
  useFormats,
  useSystemParams,
  useTimezone,
  type IRideDto,
  type IStationTimingPairStatsCriteriaDto,
} from "@ryde/shared";

import { RideMeasuredTimesContent } from "./ride-measured-times-content";

export const useRideMeasuredTimes = (
  rideDto: Partial<IRideDto> | undefined,
) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { timeFormat } = useFormats();
  const { allowShiftTimeOverrideDuringManualAction } = useSystemParams();
  const { confirmDialogResult } = useModalConfirm();
  const { updateEditingRideMetadata, updateEditingRideMetadataRideDate } =
    useRideDtoActions();
  const { isRideHasIndependentDayAdjustment } = useRideEmptyStationsAllowed(
    RideStationPanelMode.CRUD_TAB,
    rideDto,
  );
  // The dialog only reaches the screen on a later render, so two clicks inside the same tick would fire
  // two requests and open the dialog twice — the second `confirmDialogResult` overwriting the first's
  // resolver. The ref is what actually keeps the flow single-flight.
  const isFlowOpenRef = useRef(false);
  // Render-side mirror of the ref above: the button turns its icon into a spinner from the click until
  // the dialog closes, so the frames before the dialog paints do not look like nothing happened. The ref
  // cannot drive that — it does not re-render — and state alone cannot guard, hence both.
  const [isFlowOpen, setIsFlowOpen] = useState(false);
  const selectionRef = useRef<IStationTimingPreviewSelection>(
    buildStationTimingEmptySelection(),
  );

  const handleSelectionChange = useEvent(
    (selection: IStationTimingPreviewSelection): void => {
      selectionRef.current = selection;
    },
  );

  const openMeasuredTimes = useEvent(async (): Promise<void> => {
    const stations = rideDto?.metadata?.stationsInfo || [];
    const realStations = filterRealRouteStations(stations);

    if (
      !rideDto ||
      realStations.length < MINIMAL_STATIONS_COUNT_FOR_DURATION_CALCULATION
    ) {
      Log.warnAndToast(t(errors.stationTiming.notEnoughStations));
      return;
    }

    if (isFlowOpenRef.current) {
      return;
    }

    const comparisonBasis = getRideStationTimingComparisonBasis({
      rideStart: rideDto.rideStart,
      timezone,
      hoursSpread: STATION_TIMING_RIDE_COMPARABLE_HOURS_SPREAD,
    });

    // Without an anchor the backend would answer from an unfiltered pool at the ride threshold, so the
    // request is never sent rather than being silently widened.
    if (!comparisonBasis) {
      Log.warnAndToast(t(errors.stationTiming.statsNotLoaded));
      return;
    }

    const { comparisonAt, hourWindow } = comparisonBasis;
    // Only the hour window is ours to name. The date type is resolved by the backend from the same
    // anchor, and a holiday is compared against holidays rather than against its weekday, so naming the
    // anchor's weekday here would describe a comparison set that was never queried.
    const basisNote = t("stationTiming.RIDE_BASIS", {
      hours: formatStationTimingHourWindow(hourWindow),
    });

    isFlowOpenRef.current = true;
    setIsFlowOpen(true);

    // The anchor is sent, not a date type: the backend owns the canonical date-type resolution and
    // reads it off this same timestamp, so an unsaved date change moves the filter, the caption and the
    // date type together. `rideId` still travels for the ownership check only.
    const criteria: IStationTimingPairStatsCriteriaDto = {
      sourceEntity: STATION_TIMING_SOURCE_RIDE,
      rideId: rideDto.id,
      comparisonAt,
      stationIds: realStations.map(station => station.id),
      ...hourWindow,
    };

    // Started here rather than inside the dialog content so the seconds the dialog spends mounting are
    // not added to the wait, and so the criteria stay next to the ride they were built from.
    const statsPromise = StationTimingApi.getStationTimingPairStats(criteria);

    selectionRef.current = buildStationTimingEmptySelection();

    let confirmationResult: ConfirmResult;

    // `finally` rather than a line after the await: if the dialog content throws while rendering, the
    // confirm promise is never resolved by anyone, and without this the flow would stay marked open and
    // every later click on the button would be swallowed until the ride form unmounts.
    try {
      confirmationResult = await confirmDialogResult({
        title: t("stationTiming.MEASURED_TIMES_TITLE"),
        okButtonTitle: t("stationTiming.MEASURED_TIMES_APPLY"),
        width: STATION_TIMING_PREVIEW_MODAL_WIDTH,
        // Released by the content once the numbers land — there is nothing to apply until then.
        isOkButtonDisabled: true,
        customContent: (
          <RideMeasuredTimesContent
            statsPromise={statsPromise}
            stations={realStations.map(station => ({
              id: station.id,
              name: station.name,
            }))}
            plannedLegSeconds={
              collapseOptionalRouteLegsAcrossWaypoints({
                stations,
                legs: getRidePlannedLegSeconds(rideDto.metadata),
              }) ?? []
            }
            onChange={handleSelectionChange}
            basisNote={basisNote}
          />
        ),
      });
    } finally {
      isFlowOpenRef.current = false;
      setIsFlowOpen(false);
    }

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const selection = selectionRef.current;

    if (!selection.selectedLegIndexes.length) {
      Log.warnAndToast(t(errors.stationTiming.noMeasurementSelected));
      return;
    }

    // Same shift-limit gate the single station time edit runs, fed with the offset of the one end the
    // bulk apply moves. Skipped on independently adjusted rides, exactly as the single edit does.
    const mixedLegSeconds =
      expandCollapsedRouteLegsAcrossWaypoints({
        stations,
        collapsedLegs: selection.legSeconds,
      }) ?? selection.legSeconds;

    if (!isRideHasIndependentDayAdjustment) {
      const isUpdateMatchedByShiftLimits =
        isUpdatedStationTimeInsideShiftLimits({
          rideDto,
          timeOffsetMinutes: getRideStationsTimeBulkOffsetMinutes({
            rideDto,
            legSeconds: mixedLegSeconds,
          }),
          timeFormat,
          timezone,
          allowShiftTimeOverrideDuringManualAction,
        });

      if (!isUpdateMatchedByShiftLimits) {
        return;
      }
    }

    // Applied to the edited ride only — the user still saves normally. Both writers raise the metadata
    // change flag themselves; the ride-date one also follows rideStart / rideEnd, which is what the rest
    // of the CRUD tab does whenever station times move on a day-aligned ride.
    const metadata = updateRideMetadataStationsTimeBulk({
      rideDto,
      legSeconds: mixedLegSeconds,
    });

    if (isRideHasIndependentDayAdjustment) {
      updateEditingRideMetadata(rideDto.id, metadata);
    } else {
      updateEditingRideMetadataRideDate({
        rideId: rideDto.id,
        updatedMetadata: metadata,
      });
    }

    Log.infoAndToast(t("stationTiming.MEASURED_TIMES_APPLIED"));
  });

  return {
    isFlowOpen,
    openMeasuredTimes,
  };
};
